import { ModalTitle } from "@msys/ui";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import { Box, Stack, Tooltip } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import { uniqueId } from "lodash-es";
import React from "react";
import * as Yup from "yup";
import type { Props2 } from "../../../clients/graphqlTypes.js";
import { ManualSave } from "../../commons/form-fields/ManualSave.js";
import { Attachment } from "../attachments/helpers.js";
import { AllowedValuesExcludeFormWithOverride } from "./AllowedValuesExcludeFormWithOverride.js";
import { AllowedValuesMediaFormWithOverride } from "./AllowedValuesMediaFormWithOverride.js";
import {
  getValuesMediaBool,
  ValueMediaBool,
} from "./form-fields/PropertyValuesMediaBoolField.js";
import {
  createAtLeastOneShouldBeSelectedTest,
  createShouldBeInAllowedValuesTest,
} from "./modals/ItemPropsCustomizeOverrideModal.js";
import {
  AllowedValue,
  getAllowedValues,
  getPropertyType,
  isComputedProp,
} from "./properties.js";

export interface FormValues {
  defaultValue: string | string[] | number | number[] | null | undefined;
  allowedValuesExclude: string[] | number[];
  allowedValuesMedia: {
    valueAsString: string;
    media: Attachment | null;
  }[];
}

interface Props {
  property: Props2;
  initialValues: FormValues;
  handleSubmit: (newValues: FormValues) => Promise<void>;
  handleCancel: () => void;
}

export function AllowedValuesFormWithOverride({
  property,
  handleSubmit,
  handleCancel,
  initialValues,
}: Props) {
  const { t } = useTranslate(["Global", "QuoteItem"]);

  if (isComputedProp(property))
    throw new Error("Cannot customize computed property");

  const propertyType = getPropertyType(property);

  const propAllowedValues: AllowedValue[] = React.useMemo(
    () => getAllowedValues(property),
    [property]
  );

  const propValuesMediaBool: ValueMediaBool[] = React.useMemo(
    () => getValuesMediaBool(property),
    [property]
  );

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        defaultValue: Yup.mixed()
          .nullable()
          .optional()
          .label(t("Default value", { ns: "QuoteItem" }))
          .test(
            "shouldBeInAllowedValues",
            t("Not in allowed values", { ns: "QuoteItem" }),
            createShouldBeInAllowedValuesTest(property)
          ),
        allowedValuesExclude: Yup.array(Yup.mixed()).test(
          "atLeastOneShouldBeSelected",
          t("At least one value is required", {
            ns: "Global",
          }),
          createAtLeastOneShouldBeSelectedTest(property)
        ),
        allowedValuesMedia: Yup.array(
          Yup.object().shape({
            valueAsString: Yup.string(),
            media: Yup.mixed().nullable(),
          })
        ),
      }),
    [property, t]
  );

  const formId = React.useMemo(() => uniqueId(), []);

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnMount
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {formikProps => (
        <Form id={formId}>
          <Stack direction={"column"} spacing={1}>
            <ModalTitle
              title={t("Property values", { ns: "QuoteItem" })}
              headerButtons={
                <Tooltip
                  title={
                    <Box component="span" sx={{ whiteSpace: "pre-line" }}>
                      {t(
                        "If the property has a list of allowed values, you can see them here. If you deselect some values they will be excluded as options for this template. Values that are deselected can not be set for the property in the quote and will not be available to wizard users for selection. You can edit the picture that should be shown on the wizard for each value. You can define a default for the property so that it will be preselected in the quote as an initial value.",
                        {
                          ns: "QuoteItem",
                        }
                      )}
                    </Box>
                  }
                >
                  <InfoIcon />
                </Tooltip>
              }
              titleProps={{ sx: { p: "0 !important" }, variant: "h3" }}
            />

            <AllowedValuesExcludeFormWithOverride
              propAllowedValues={propAllowedValues}
              propertyType={propertyType}
              property={property}
            />

            <AllowedValuesMediaFormWithOverride
              propValuesMediaBool={propValuesMediaBool}
              propertyType={propertyType}
            />

            <ManualSave
              disabled={formikProps.isSubmitting}
              disabledSubmit={!formikProps.isValid || !formikProps.dirty}
              onCancel={handleCancel}
            />
          </Stack>
        </Form>
      )}
    </Formik>
  );
}
